<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<div class="background-light">
			<main-content
				:title="page.title || page.header"
				:content="page.content"
				:intro-image="{
					image: page.introductionImage,
					imageWebp: page.introductionImageWebp,
				}"
			/>
			<div class="row">
				<div class="horizontal-divider large" />
			</div>
			<section class="blog-container">
				<div class="row magazine-row">
					<div v-for="item in itemsPaginated" :key="item.ID" class="magazine-item columns column4">
						<nuxt-link :to="localePath(`/${item.filename}`)" class="image-link">
							<picture>
								<source :srcset="item.overviewImageWebp" type="image/webp" />
								<source :srcset="item.overviewImage" />
								<img :src="item.overviewImage" :alt="item.overviewImageAlt" loading="lazy" />
							</picture>
						</nuxt-link>
						<div class="content">
							<strong v-if="item.category" class="tag">{{ item.category }}</strong>
							<h3>
								<nuxt-link :to="localePath(`/${item.filename}`)">
									{{ item.title || item.header }}
								</nuxt-link>
							</h3>
							<div v-parse-links v-html="item.overviewContent" />
							<nuxt-link :to="localePath(`/${item.filename}`)" class="text-button">
								<span>{{ $t('readMore') }}</span>
								<font-awesome-icon :icon="`fal fa-chevron-right`" size="1x" />
							</nuxt-link>
						</div>
					</div>
				</div>
				<div v-if="items.length > itemsPaginated.length" class="row align-center read-more">
					<div class="columns column12">
						<button class="button" @click="currentPage++">
							{{ $t('readMore') }}
						</button>
					</div>
				</div>
			</section>

			<contentblocks :items="page.contentblocks" />
			<frequently-asked-questions
				v-if="page.frequentlyAskedQuestion"
				:items="page.frequentlyAskedQuestion.faqitems"
				:header="page.frequentlyAskedQuestion.header"
			/>
		</div>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();

useRemoveFormitable();

const { data: items } = await useWebsiteFetch('magazine', {
	query: { language: locale.value },
	key: `${locale.value}/magazine`,
});

const currentPage = ref(1);
const itemsPerPage = 5;

const itemsPaginated = computed(() => {
	return items.value.slice(0, currentPage.value * itemsPerPage);
});
</script>

<style lang="scss" scoped>
.blog-container {
	padding-top: 25px;

	.read-more {
		margin: 20px 0;
	}
}

.horizontal-divider {
	background: #3c3834;
	opacity: 1;
	height: 1px;
	width: calc(100% - 40px);
	margin: 0 auto;
}

.tag {
	display: inline-block;
	width: 100%;
	text-transform: uppercase;
	margin: 0 0 20px;
}

h2,
h3,
h4 {
	margin: 0 0 10px;

	a {
		text-decoration: none;
		color: var(--header-color);

		&:hover {
			color: var(--header-color);
		}

		&::after {
			display: none;
		}
	}
}

.magazine-row {
	flex-flow: row wrap;
	justify-content: center;
	align-items: stretch;
	padding: 0 20px;

	.magazine-item {
		width: calc((100% / 3) + 20px);
		border-right: 1px solid #3c3834;
		padding: 0 50px;
		margin: 40px 0;
		position: relative;

		h3 a {
			color: var(--header-color-quaternary);
		}

		&:first-child {
			padding: 0 40px 0 0;
			width: calc((100% / 3) - 40px);

			h3 a {
				color: var(--cta-color-blue);
			}

			.content {
				.text-button {
					left: 0;
				}
			}

			.image-link {
				&:hover img {
					transform: translate(-24px, 0);
				}

				&::after {
					display: none;
				}
			}
		}

		&:last-child,
		&:nth-child(3) {
			border: none;
			padding: 0 0 0 40px;
			width: calc((100% / 3) - 40px);

			.image-link {
				&:hover img {
					transform: rotate(6deg);
				}
			}

			h3 a {
				color: var(--header-color-secondary);
			}
		}

		&:nth-child(4),
		&:nth-child(5) {
			width: 100%;
			display: flex;
			flex-flow: row wrap;
			padding: 30px;
			justify-content: flex-start;
			align-items: stretch;
			background: var(--header-color-quaternary);
			color: var(--body-color);
			border: none;
			position: relative;
			margin-bottom: 60px;

			h3 a {
				color: var(--body-color);
			}

			&::after {
				display: block;
				position: absolute;
				left: 60px;
				right: 60px;
				bottom: -50px;
				content: '';
				height: 1px;
				background: #3c3834;
			}

			.content {
				width: 70%;
				padding: 80px 100px;

				.text-button {
					left: auto;
					bottom: auto;
				}
			}

			.image-link {
				width: 30%;

				&::after {
					display: none;
				}
			}

			h2,
			h3,
			h4 {
				color: var(--body-color);
				max-width: 600px;
				margin: 0 0 10px;
			}

			h3 {
				font-size: var(--h2-font-size);
				font-family: var(--header-font-family);
			}
		}

		&:nth-child(5) {
			flex-flow: row-reverse wrap;
			background: var(--cta-color-blue);
		}

		.image-link {
			overflow: visible;

			img {
				transition: all 0.3s ease-in-out;
				aspect-ratio: 5 / 6;
			}

			&::after {
				display: none;
			}

			&:hover img {
				transform: scale(1.07);
			}
		}

		.date {
			margin: 20px 0 0;
		}
	}

	:deep(p) {
		margin: 20px 0 50px;
	}

	.content {
		padding: 50px 0;

		.text-button {
			position: absolute;
			bottom: 0;
			left: 50px;
			background: none;
			display: flex;
			flex-flow: row nowrap;
			justify-content: space-between;
			align-items: center;
			padding: 0 0 5px;

			svg {
				font-size: 18px;
				margin: 1px 0 -1px 15px;
			}
		}
	}

	h3 {
		font-size: var(--h4-font-size);
		max-width: 400px;
	}
}

@media (max-width: 880px) {
	.magazine-row {
		flex-flow: column wrap;

		.magazine-item {
			width: calc(100% - 40px) !important;
			border: none !important;
			padding: 0 20px !important;
			margin: 0 auto;

			.content {
				.text-button {
					left: 20px !important;
					bottom: 40px;
					justify-content: flex-start;
				}
			}

			&:nth-child(4),
			&:nth-child(5) {
				padding: 20px !important;

				&::after {
					bottom: -30px;
				}

				h3 {
					font-size: 2em;
				}

				.image-link {
					width: 100%;
				}

				.content {
					width: 100%;
					padding: 40px 20px;
				}
			}
		}
	}
}
</style>
